
























import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Card from '@/components/default/Card.vue';
import { QuotationDetailedDto, VolumeDiscount, VolumeDiscountClient, QuotationDiscountLine, ContactsLookupQuery } from '@/data/web-api-client';
import Datagrid from '@/components/default/Datagrid.vue';
import Helpers, { sleep } from '@/helpers/Helper';
import dxDataGrid from 'devextreme/ui/data_grid';
import { CompanyConfig } from '@/installationConfig';

@Component({
  components: {
    Card,
    Datagrid,
  },
})
export default class QuotationDiscountCard extends Vue {
  @Prop({ required: true })
  private quotation!: QuotationDetailedDto;

  @Prop({ required: true })
  private total!: number;

  @Prop()
  private projectIsOnHold!: boolean;

  selectedVolumeDiscount: VolumeDiscount | null = null;
  lowerDiscountPercentage: number = 0;

  private volumeDiscounts: VolumeDiscount[] = [];
  private selectedRowIndexes: number[] = [];
  private updating = false;
  showGrid: boolean = false;
  lastTotal: number = 0;

  async mounted() {
    this.volumeDiscounts = await new VolumeDiscountClient(undefined, this.$axios).getAll();

    this.checkQuotationVolumeDiscount();
    this.setDiscountLines();
  }

  onInitialized(e: any) {
    (this.$refs?.discountsGrid as Datagrid).toggleFilter();
  }

  onCellPrepared(e: any) {
    if (this.projectIsOnHold || e?.data?.name !== 'Handmatige korting') {
      const editIcon = $(e.cellElement).find('.dx-icon-edit');

      editIcon?.remove();
    }
  }

  async onContentReady(e: any) {
    if (this.updating) {
      return;
    }

    const table = e.component as dxDataGrid;
    const firstRow = table.getVisibleRows()[0] ?? null;

    if (this.selectedRowIndexes.length > 0) {
      await table.selectRowsByIndexes([...this.selectedRowIndexes]);

      this.selectedRowIndexes = [];
    } else if (table.getSelectedRowsData().length === 0 && firstRow) {
      await table.selectRowsByIndexes([firstRow.rowIndex!]);
    }
  }

  async onRowUpdated(e: any) {
    if (this.updating) {
      return;
    }

    await this.checkDiscounts();
  }

  async onSelectionChanged(e: any) {
    if (this.updating) {
      return;
    }

    await this.checkDiscounts();
  }

  private checkQuotationVolumeDiscount() {
    if (this.selectedVolumeDiscount && this.selectedVolumeDiscount.upperLimit == null) {
      this.selectedVolumeDiscount.upperLimit == Number.MAX_VALUE;
    }

    if (
      !this.selectedVolumeDiscount ||
      this.selectedVolumeDiscount == null ||
      this.total > this.selectedVolumeDiscount?.upperLimit! ||
      this.total < this.selectedVolumeDiscount?.lowerLimit!
    ) {
      this.selectedVolumeDiscount =
        this.volumeDiscounts.find((vd) => {
          const upperLimit = vd.upperLimit == null ? Number.MAX_VALUE : vd.upperLimit;
          return vd.lowerLimit! < this.total! && upperLimit > this.total!;
        }) ?? this.volumeDiscounts[this.volumeDiscounts.length - 1];
    }

    let previousVolumeDiscountIndex = this.volumeDiscounts.indexOf(this.selectedVolumeDiscount) - 1;
    if (previousVolumeDiscountIndex < 0) {
      this.lowerDiscountPercentage = 0;
    } else {
      const previousSelectedVolumeDiscount = this.volumeDiscounts[previousVolumeDiscountIndex];
      this.lowerDiscountPercentage = previousSelectedVolumeDiscount.discountPercentage!;
    }
  }

  private async checkDiscounts() {
    if (this.updating) {
      return;
    }

    const table = this.gridInstance;

    if (!table) {
      return;
    }

    table.beginCustomLoading('Kortingen berekenen...');

    this.updating = true;

    const selectedRows: QuotationDiscountLine[] = await table.getSelectedRowsData();
    const allRows = table.getVisibleRows();

    for (const row of allRows) {
      let totalWithDiscount = 0;
      let basePrice = 0;
      const lastSelected = selectedRows.filter((r) => r.key! < row.data.key).sort((a, b) => (a.key! < b.key! ? 1 : -1))[0];
      const thisIsSelected = row.isSelected;

      if (lastSelected) {
        totalWithDiscount = lastSelected.totalWithDiscount! - lastSelected.totalWithDiscount! * (row.data.discountPercentage / 100);
        basePrice = lastSelected.totalWithDiscount!;
      } else {
        totalWithDiscount = row.data.total - row.data.total * (row.data.discountPercentage / 100);
        basePrice = row.data.total;
      }

      if (!thisIsSelected) {
        totalWithDiscount = basePrice;
      }

      row.data.isSelected = thisIsSelected;

      table.cellValue(row.rowIndex!, 'totalWithDiscount', totalWithDiscount);
      table.cellValue(row.rowIndex!, 'total', basePrice);

      await table.saveEditData();
    }

    this.$emit('discountChecked');

    this.updating = false;

    table.endCustomLoading();
  }

  private calculateVolumeDiscountPercentage(): number {
    if (this.total == 0 || !this.selectedVolumeDiscount) {
      return 0;
    }

    const total = this.total;
    const upperLimit = this.selectedVolumeDiscount!.upperLimit ?? Number.MAX_VALUE;
    const lowerLimit = this.selectedVolumeDiscount!.lowerLimit! < 0 ? 0 : this.selectedVolumeDiscount!.lowerLimit!;
    const maxDiscountRate = this.selectedVolumeDiscount!.discountPercentage!;

    const percentageRange = maxDiscountRate - this.lowerDiscountPercentage;
    const baseTotal = total - lowerLimit;
    const baseUpper = upperLimit - lowerLimit;

    const percentageRangePoint = baseTotal / baseUpper;
    let discountPercentage = percentageRangePoint * percentageRange + this.lowerDiscountPercentage;

    if (upperLimit == Number.MAX_VALUE) {
      discountPercentage = maxDiscountRate;
    }

    if (discountPercentage > maxDiscountRate) {
      discountPercentage = maxDiscountRate;
    }

    return Number(discountPercentage.toFixed(2));
  }

  private setSelectedRowIndexes() {
    this.selectedRowIndexes =
      this.gridInstance
        ?.getVisibleRows()
        .map((row, i) => ({ ...row, i }))
        .filter((row) => row.isSelected)
        .map(({ i, ...row }) => i) ?? [];
  }

  private setDiscountLines() {
    if (this.discountLines?.length! > 0) {
      this.selectedRowIndexes =
        this.discountLines
          ?.map((discount, i) => ({ ...discount, i }))
          .filter((discount) => discount.isSelected)
          .map(({ i, ...discount }) => i) ?? [];

      this.showGrid = true;
      return;
    }

    const quotationTotal = this.total;
    const customer = this.quotation.customer;

    let discounts: QuotationDiscountLine[] = [
      new QuotationDiscountLine({
        key: 1,
        name: 'Basis',
        total: quotationTotal,
        discountPercentage: 0,
        totalWithDiscount: 0,
        margin: 0,
      }),
      new QuotationDiscountLine({
        key: 2,
        name: `Volumekorting`,
        total: quotationTotal,
        discountPercentage: this.calculateVolumeDiscountPercentage(),
        totalWithDiscount: 0,
        margin: 0,
      }),
      new QuotationDiscountLine({
        key: 3,
        name: 'Klantenkorting',
        total: quotationTotal,
        discountPercentage: customer?.billingDetails?.discountPercentage ?? 0,
        totalWithDiscount: 0,
        margin: 0,
      }),
      new QuotationDiscountLine({
        key: 4,
        name: 'Handmatige korting',
        total: quotationTotal,
        discountPercentage: 0,
        totalWithDiscount: 0,
        margin: 0,
      }),
    ];

    if (CompanyConfig.companyName == 'OneSkin') {
      const discountLineIndex = discounts.findIndex((dl) => dl.key == 2);

      if (discountLineIndex > -1) {
        discounts.splice(discountLineIndex, 1);
      }
    }

    for (const discount of discounts) {
      const result = discount.total! - discount.total! * (discount.discountPercentage! / 100);

      discount.totalWithDiscount = result;
    }

    this.showGrid = true;

    this.quotation.discountLines = discounts;
  }

  @Watch('total')
  async onTotalChange(newValue: number) {
    if (isNaN(newValue) || this.lastTotal == newValue) {
      return;
    }

    this.lastTotal = newValue;

    this.setSelectedRowIndexes();
    this.checkQuotationVolumeDiscount();

    if (this.quotation.discountLines) {
      const firstRowIndex = this.quotation.discountLines?.findIndex((dl) => dl.name == 'Basis') ?? -1;

      if (firstRowIndex > -1) {
        const firstDiscountLine = this.quotation.discountLines[firstRowIndex];

        firstDiscountLine.total = newValue;

        this.quotation.discountLines[firstRowIndex] = firstDiscountLine;
      }
    }

    const baseRow = this.gridInstance?.getVisibleRows()[0];

    if (baseRow) {
      this.gridInstance?.cellValue(baseRow.rowIndex!, 'total', this.total);

      await this.gridInstance?.saveEditData();

      if (this.volumeDiscountLine) {
        this.volumeDiscountLine.discountPercentage = this.calculateVolumeDiscountPercentage();
      }
    }
  }

  @Watch('quotation.customer')
  async onQuotationChange() {
    this.setSelectedRowIndexes();

    this.quotation.discountLines!.find((dl) => dl.key === 3)!.discountPercentage = this.quotation.customer?.billingDetails?.discountPercentage ?? 0;
  }

  @Watch('selectedVolumeDiscount')
  async onSelectedVolumeDiscountChange() {
    if (this.volumeDiscountLine) {
      this.volumeDiscountLine.discountPercentage = this.calculateVolumeDiscountPercentage();
    }
  }

  get discountLines() {
    if (!this.quotation.discountLines) {
      return [];
    }
    let discounts = this.quotation.discountLines;
    if (CompanyConfig.companyName == 'OneSkin') {
      const discountLineIndex = discounts.findIndex((dl) => dl.key == 2);

      if (discountLineIndex > -1) {
        discounts.splice(discountLineIndex, 1);
      }
    }
    return discounts;
  }

  get volumeDiscountLine() {
    if (!this.quotation || !this.quotation.discountLines) {
      return;
    }

    return this.quotation.discountLines!.find((dl) => dl.key === 2);
  }

  get columns() {
    return [
      {
        dataField: 'name',
        caption: '',
        allowSorting: false,
        editorOptions: {
          disabled: true,
        },
      },
      {
        dataField: 'discountPercentage',
        caption: 'Korting (%)',
        calculateDisplayValue(e: QuotationDiscountLine) {
          return Helpers.formatNumber(e.discountPercentage!) + '%';
        },
        allowSorting: false,
      },
      {
        dataField: 'total',
        caption: 'Basisprijs',
        format: {
          type: 'currency',
          precision: 2,
        },
        allowSorting: false,
        editorOptions: {
          disabled: true,
        },
      },
      {
        dataField: 'discount',
        caption: 'Korting',
        calculateCellValue: (e: QuotationDiscountLine) => {
          const result = e.total! * (e.discountPercentage! / 100);

          return result;
        },
        format: {
          type: 'currency',
          precision: 2,
        },
        allowSorting: false,
        editorOptions: {
          disabled: true,
        },
      },
      {
        dataField: 'totalWithDiscount',
        caption: 'Totaal',
        format: {
          type: 'currency',
          precision: 2,
        },
        allowSorting: false,
        editorOptions: {
          disabled: true,
        },
      },
      {
        dataField: 'margin',
        caption: 'Marge',
        calculateCellValue: (e: QuotationDiscountLine) => {
          return 0;
        },
        format: {
          type: 'currency',
          precision: 2,
        },
        allowSorting: false,
        editorOptions: {
          disabled: true,
        },
      },
    ];
  }

  get gridInstance() {
    if (!this.showGrid) {
      return;
    }

    return (this.$refs.discountsGrid as any)?.$refs.discountsTable?.instance as dxDataGrid;
  }
}
